import { useState, useEffect } from "react";
import {
  ActionIcon, Anchor, Avatar, Badge, Box, Button, Card, Center, Container, Group, HoverCard, Loader, Modal, Stack,
  Table, Text, TextInput, Textarea, Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconBuilding, IconChevronRight, IconEdit, IconLink, IconPlus, IconSearch, IconTrash, IconUser } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { getClients, createClient, updateClient, searchCandidates } from "../api";
import { useIsMobile } from "../hooks/useIsMobile";
import TelLink from "../components/TelLink";

const stopTap = (e) => e.stopPropagation();

function ContactRow({ contact, onRemove, t }) {
  return (
    <Group gap="xs" p="xs" style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: "var(--mantine-radius-sm)" }}>
      <div style={{ flex: 1 }}>
        <Group gap="xs">
          <Text size="sm" fw={500}>{contact.name}</Text>
          {contact.linkedCandidateId && (
            <Badge size="xs" variant="light" color="green" leftSection={<IconLink size={10} />}>{t("linked")}</Badge>
          )}
        </Group>
        {contact.role && <Text size="xs" c="dimmed">{contact.role}</Text>}
        <Group gap="xs" wrap="wrap">
          {contact.mobilePhone && <TelLink label={t("mobile")} value={contact.mobilePhone} />}
          {contact.directDial && <TelLink label={t("direct_dial")} value={contact.directDial} />}
          {contact.phone && <TelLink label={t("phone")} value={contact.phone} />}
          {contact.email && (
            <Text size="xs" c="dimmed">
              <Anchor href={`mailto:${contact.email}`} onClick={stopTap}>{contact.email}</Anchor>
            </Text>
          )}
        </Group>
        {contact.assistantName && (
          <Text size="xs" c="dimmed">
            {t("contact_assistant", { name: contact.assistantName })}
            {contact.assistantPhone ? ` (${t("phone")}: ${contact.assistantPhone})` : ""}
            {contact.assistantMobile ? ` (${t("mobile")}: ${contact.assistantMobile})` : ""}
          </Text>
        )}
      </div>
      {onRemove && (
        <ActionIcon variant="subtle" color="red" size="sm" onClick={onRemove}>
          <IconTrash size={14} />
        </ActionIcon>
      )}
    </Group>
  );
}

function ClientForm({ initialValues, onSubmit, onCancel, saving, t }) {
  const form = useForm({
    initialValues: initialValues ?? { name: "", industry: "", notes: "", contacts: [] },
    validate: { name: (v) => (v.trim() ? null : "Name is required") },
  });

  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidateResults, setCandidateResults] = useState([]);
  const [searching, setSearching] = useState(false);

  async function handleCandidateSearch(q) {
    setCandidateSearch(q);
    if (!q.trim() || q.trim().length < 2) { setCandidateResults([]); return; }
    setSearching(true);
    try {
      const results = await searchCandidates(q);
      setCandidateResults(Array.isArray(results) ? results.slice(0, 8) : []);
    } catch { setCandidateResults([]); }
    finally { setSearching(false); }
  }

  function addLinkedContact(c) {
    setCandidateSearch("");
    setCandidateResults([]);
    if (!c?.id) return;
    const existing = form.values.contacts;
    if (existing.some((x) => x.linkedCandidateId === c.id)) {
      notifications.show({
        color: "blue",
        title: t("already_linked"),
        message: t("already_linked_msg", { name: c.name }),
      });
      return;
    }
    // Snapshot display fields from the candidate at link time so the detail
    // view doesn't need to dereference. The candidate remains the source of
    // truth — re-linking refreshes the snapshot.
    form.setFieldValue("contacts", [
      ...existing,
      {
        linkedCandidateId: c.id,
        name: c.name,
        role: c.title || undefined,
        email: c.email || undefined,
        mobilePhone: c.phone || undefined,
      },
    ]);
  }

  function removeContact(index) {
    const contacts = [...form.values.contacts];
    contacts.splice(index, 1);
    form.setFieldValue("contacts", contacts);
  }

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack>
        <TextInput label={t("company_name")} placeholder={t("company_name_placeholder")} required {...form.getInputProps("name")} />
        <TextInput label={t("industry")} placeholder={t("industry_placeholder")} {...form.getInputProps("industry")} />
        <Textarea label={t("notes")} placeholder={t("notes_placeholder")} rows={3} {...form.getInputProps("notes")} />

        {/* Contacts — link existing candidates only */}
        <div>
          <Text size="sm" fw={500} mb={2}>{t("contacts")}</Text>
          <Text size="xs" c="dimmed" mb="xs">{t("link_contact_help")}</Text>
          <Stack gap="xs" mb="xs">
            {form.values.contacts.map((c, i) => (
              <ContactRow key={i} contact={c} onRemove={() => removeContact(i)} t={t} />
            ))}
          </Stack>
          <Card p="xs" withBorder>
            <TextInput
              size="xs"
              placeholder={t("link_to_candidate")}
              leftSection={<IconSearch size={12} />}
              value={candidateSearch}
              onChange={(e) => handleCandidateSearch(e.currentTarget.value)}
              rightSection={searching ? <Loader size={12} /> : null}
            />
            {candidateResults.length > 0 && (
              <Stack gap={2} mt={4} style={{ border: "1px solid var(--mantine-color-default-border)", borderRadius: "var(--mantine-radius-sm)", maxHeight: 200, overflow: "auto" }}>
                {candidateResults.map((c) => (
                  <Group key={c.id} gap="xs" p={4} style={{ cursor: "pointer" }} onClick={() => addLinkedContact(c)}
                    onMouseEnter={(e) => e.currentTarget.style.background = "var(--mantine-color-gray-light)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
                    <IconLink size={12} />
                    <div style={{ flex: 1 }}>
                      <Text size="xs" fw={500}>{c.name}</Text>
                      {c.title && <Text size="xs" c="dimmed">{c.title}</Text>}
                    </div>
                  </Group>
                ))}
              </Stack>
            )}
          </Card>
        </div>

        <Group justify="flex-end">
          <Button variant="default" onClick={onCancel}>{t("cancel")}</Button>
          <Button type="submit" loading={saving}>{t("save")}</Button>
        </Group>
      </Stack>
    </form>
  );
}

export default function ClientList() {
  const { t } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  async function loadClients() {
    setLoading(true);
    try {
      const data = await getClients();
      setClients(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load clients", e);
    } finally {
      setLoading(false);
    }
  }

  // Note: a contact's "Client" role is now computed server-side from the
  // ClientContact link, so no per-candidate role write is needed here.

  async function handleCreate(values) {
    setSaving(true);
    try {
      const dto = {
        name: values.name.trim(),
        industry: values.industry.trim() || undefined,
        notes: values.notes.trim() || undefined,
        contacts: values.contacts.length > 0 ? values.contacts : undefined,
      };
      const client = await createClient(dto);
      setClients((prev) => [...prev, client].sort((a, b) => a.name.localeCompare(b.name)));
      setCreateOpen(false);
      notifications.show({ title: "Client created", message: client.name });
    } catch (e) {
      notifications.show({ color: "red", title: "Error", message: "Failed to create client" });
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(values) {
    if (!editClient) return;
    setSaving(true);
    try {
      const dto = {
        name: values.name.trim(),
        industry: values.industry.trim() || undefined,
        notes: values.notes.trim() || undefined,
        contacts: values.contacts,
      };
      const updated = await updateClient(editClient.id, dto);
      setClients((prev) => prev.map((c) => (c.id === editClient.id ? updated : c)));
      setEditClient(null);
      notifications.show({ title: "Client updated", message: updated.name });
    } catch (e) {
      notifications.show({ color: "red", title: "Error", message: "Failed to update client" });
    } finally {
      setSaving(false);
    }
  }

  const isMobile = useIsMobile();

  if (loading) {
    return <Center py="xl"><Loader /></Center>;
  }

  return (
    <Container size="md" py="md">
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={3}>{t("clients")}</Title>
        <Button leftSection={<IconPlus size={16} />} size="sm" onClick={() => setCreateOpen(true)}>
          {t("add_client")}
        </Button>
      </Group>

      {clients.length === 0 ? (
        <Card withBorder p="xl" maw={460} mx="auto" mt="md">
          <Stack gap="sm" align="center" ta="center">
            <Avatar size="lg" radius="xl" color="gray"><IconBuilding size={24} /></Avatar>
            <Text fw={600}>{t("clients_empty_title")}</Text>
            <Text size="sm" c="dimmed">{t("clients_empty_help")}</Text>
            <Button mt="xs" leftSection={<IconPlus size={16} />} onClick={() => setCreateOpen(true)}>
              {t("add_client")}
            </Button>
          </Stack>
        </Card>
      ) : isMobile ? (
          <Stack gap="xs">
            {clients.map((client) => (
              <Card
                key={client.id}
                p="sm"
                radius="sm"
                withBorder
                style={{ cursor: "pointer" }}
                onClick={() => setLocation(`/clients/${client.id}`)}
              >
                <Group gap="sm" wrap="nowrap">
                  <Avatar size="sm" radius="xl" color="gray" style={{ flexShrink: 0 }}>
                    <IconBuilding size={16} />
                  </Avatar>
                  <Box style={{ minWidth: 0, flex: 1 }}>
                    <Text size="sm" fw={500} truncate>{client.name}</Text>
                    {client.industry && <Text size="xs" c="dimmed" truncate>{client.industry}</Text>}
                    {(client.contacts ?? []).length > 0 && (
                      <Group gap={4} mt={4}>
                        {client.contacts.slice(0, 3).map((c, i) => (
                          <Badge key={i} size="xs" variant="light" leftSection={<IconUser size={10} />}>
                            {c.name}
                          </Badge>
                        ))}
                        {client.contacts.length > 3 && (
                          <Badge size="xs" variant="light" color="gray">+{client.contacts.length - 3}</Badge>
                        )}
                      </Group>
                    )}
                  </Box>
                  <ActionIcon variant="subtle" size="sm" style={{ flexShrink: 0 }}>
                    <IconChevronRight size={14} />
                  </ActionIcon>
                </Group>
              </Card>
            ))}
          </Stack>
        ) : (
          <Table.ScrollContainer minWidth={500}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("name")}</Table.Th>
                  <Table.Th>{t("industry")}</Table.Th>
                  <Table.Th>{t("contacts")}</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {clients.map((client) => (
                  <Table.Tr key={client.id}>
                    <Table.Td>
                      <Group gap="sm">
                        <IconBuilding size={16} color="gray" />
                        <Text size="sm" fw={500}>{client.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{client.industry ?? "—"}</Text>
                    </Table.Td>
                    <Table.Td>
                      {(client.contacts ?? []).length > 0 ? (
                        <Group gap={4}>
                          {client.contacts.map((c, i) => (
                            <HoverCard key={i} width={260} shadow="md" withArrow openDelay={200}>
                              <HoverCard.Target>
                                <Badge size="xs" variant="light" leftSection={<IconUser size={10} />} style={{ cursor: "default" }}>
                                  {c.name}
                                </Badge>
                              </HoverCard.Target>
                              <HoverCard.Dropdown>
                                <Text size="sm" fw={500}>{c.name}</Text>
                                {c.role && <Text size="xs" c="dimmed">{c.role}</Text>}
                                <Stack gap={2} mt={4}>
                                  {c.mobilePhone && <Text size="xs">{t("mobile")}: {c.mobilePhone}</Text>}
                                  {c.directDial && <Text size="xs">{t("direct_dial")}: {c.directDial}</Text>}
                                  {c.phone && <Text size="xs">{t("phone")}: {c.phone}</Text>}
                                  {c.email && <Text size="xs">{c.email}</Text>}
                                  {c.assistantName && (
                                    <Text size="xs" c="dimmed" mt={2}>
                                      {t("contact_assistant", { name: c.assistantName })}
                                      {c.assistantPhone ? ` (${t("phone")}: ${c.assistantPhone})` : ""}
                                      {c.assistantMobile ? ` (${t("mobile")}: ${c.assistantMobile})` : ""}
                                    </Text>
                                  )}
                                </Stack>
                              </HoverCard.Dropdown>
                            </HoverCard>
                          ))}
                        </Group>
                      ) : (
                        <Text size="xs" c="dimmed">{t("no_contacts")}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon variant="subtle" size="sm" onClick={() => setEditClient(client)}>
                        <IconEdit size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
      )}

      {/* Create Modal */}
      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title={t("add_client")} size="lg">
        <ClientForm onSubmit={handleCreate} onCancel={() => setCreateOpen(false)} saving={saving} t={t} />
      </Modal>

      {/* Edit Modal */}
      <Modal opened={!!editClient} onClose={() => setEditClient(null)} title={`${editClient?.name ?? ""}`} size="lg">
        {editClient && (
          <ClientForm
            initialValues={{
              name: editClient.name ?? "",
              industry: editClient.industry ?? "",
              notes: editClient.notes ?? "",
              contacts: editClient.contacts ?? [],
            }}
            onSubmit={handleUpdate}
            onCancel={() => setEditClient(null)}
            saving={saving}
            t={t}
          />
        )}
      </Modal>
    </Stack>
    </Container>
  );
}
